/**
 * Serialised access to /api/cart.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 * Budflow's `addtocart` RPC DELETES and RE-INSERTS every orderline on the order
 * for each write (which is also why orderline ids are ephemeral and the cart is
 * keyed on product_id — see mergeStableCartOrder in side-cart.tsx). Two writes
 * in flight against the same order therefore race, and the loser dies on the
 * backend's unique constraint:
 *
 *   duplicate key value violates unique constraint "uniqueproductorderline"
 *
 * Reproduced 2026-09-04 on catalystantioch: three concurrent ADDs of distinct
 * products landed two lines, not three. The same three ADDs issued sequentially
 * landed all three. The failure is silent to the shopper — /api/cart answers
 * HTTP 200 with the error in the BODY, and the optimistic UI has already drawn
 * the product into the side-cart, so the item appears and then vanishes on the
 * next GET sync. Clicking "Add to Cart" on several products in quick succession
 * is all it takes.
 *
 * Per-card `isLoading` state does not help: it disables the card you clicked,
 * not the other twenty on the grid.
 *
 * ── What this does ────────────────────────────────────────────────────────
 * Funnels every /api/cart request through one promise chain, so at most one is
 * ever in flight. Reads are queued too, deliberately: a GET issued after a write
 * must observe that write, and the cart GET is what reconciles the optimistic
 * line against the server.
 *
 * The chain never rejects — a failed request settles it so the next queued call
 * still runs. Callers see their own rejection, exactly as with plain `fetch`.
 *
 * ── Scope ─────────────────────────────────────────────────────────────────
 * Client-side only, which is the right layer: the race is one shopper clicking
 * quickly in one tab, and this is the single choke point for all 23 call sites.
 * It does NOT protect against the same order being written from two tabs or two
 * devices. Only an atomic upsert in `addtocart` fixes that — worth raising with
 * Andy/Tom, since Embarc's cart carries the identical exposure (its
 * cart-helper.ts retries invalid-order errors but does not serialise).
 */

/** Tail of the queue. Always resolved-or-settled, never rejected. */
let chain: Promise<unknown> = Promise.resolve();

/**
 * Drop-in replacement for `fetch` against /api/cart.
 *
 * Same signature and same return value as `fetch`, so call sites only change
 * the function name.
 */
export function cartFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // Run whether or not the previous call succeeded — one failed write must not
  // strand every later add behind it.
  const run = () => fetch(input, init);
  const result = chain.then(run, run);

  // Swallow on the CHAIN only. `result` keeps its rejection for the caller.
  chain = result.then(
    () => undefined,
    () => undefined,
  );

  return result;
}
